// Header.js - Updated with cart count
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useCart } from '../../contexts/CartContext';
import { Menu, Bell, User, LogOut, ShoppingCart, Package, Globe, X, Mail, Phone, LogIn, UserPlus } from 'lucide-react';
import logo from '../../assets/styles/crossdeal Transparent1.png';

const Header = ({ onToggleSidebar }) => {
  const { user, logout, isBuyer, isSeller, isAdmin } = useAuth();
  const { notifications, unreadCount } = useNotifications();
  const { cartCount } = useCart(); // Get cart count from context
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      {/* Combined Header - Black top bar and main header merged */}
      <header className="bg-white border-b shadow-sm w-full fixed top-0 left-0 right-0 z-50">
        {/* Top Contact Bar - Simplified without icons */}
        <div className="bg-black text-white w-full">
          <div className="w-full px-4 py-1">
            <div className="flex flex-col sm:flex-row justify-between items-center text-xs max-w-7xl mx-auto space-y-1 sm:space-y-0">
              <div className="flex flex-col sm:flex-row items-center sm:space-x-4 space-y-1 sm:space-y-0">
                <div className="flex items-center space-x-1">
                  <Mail className="w-3 h-3 text-orange-400" />
                  <span className="text-xs">sales@crossdeal.com</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Phone className="w-3 h-3 text-orange-400" />
                  <span className="text-xs">+91 98765 43210</span>
                </div>
              </div>

              {/* Empty div to maintain layout */}
              <div></div>
            </div>
          </div>
        </div>

        {/* Main Header Section - Now with icons */}
        <div className="w-full px-4">
          <div className="flex items-center justify-between py-2 max-w-7xl mx-auto">
            {/* Logo and Menu Button */}
            <div className="flex items-center space-x-4">
              {user && (
                <button
                  onClick={onToggleSidebar}
                  className="lg:hidden mr-2 text-gray-600 hover:text-orange-500 transition-colors"
                >
                  <Menu className="h-5 w-5" />
                </button>
              )}

              <Link to="/" className="flex items-center space-x-0 group">
                <div className="relative">
                  <img 
                    src={logo} 
                    alt="CrossDeal Logo" 
                    className="h-15 w-16 object-contain"
                  />
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-xl font-bold text-black tracking-tight">
                    CROSSDEAL
                  </span>
                  <span className="text-[10px] text-gray-500 font-medium tracking-wider">
                    YOUR BRIDGE TO GLOBAL TRADE
                  </span>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6">
              {/* Show navigation links only when user is logged in */}
              {user ? (
                <>
                  {/* Icons for logged-in users */}
                  <div className="flex items-center space-x-4 pl-4 border-l border-gray-200">
                    <Globe className="w-4 h-4 cursor-pointer text-gray-600 hover:text-orange-500 transition-colors" title="Language" />

                    {isBuyer && (
                      <Link to="/buyer/cart" className="relative" title="Shopping Cart">
                        <ShoppingCart className="w-4 h-4 text-gray-600 hover:text-orange-500 transition-colors" />
                        {cartCount > 0 && (
                          <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                            {cartCount > 9 ? '9+' : cartCount}
                          </span>
                        )}
                      </Link>
                    )}

                    <button className="relative" title="Notifications">
                      <Bell className="w-4 h-4 text-gray-600 hover:text-orange-500 transition-colors" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </button>

                    <button
                      onClick={() => setIsProfileOpen(!isProfileOpen)}
                      className="relative"
                      title="Profile"
                    >
                      <User className="w-4 h-4 text-gray-600 hover:text-orange-500 transition-colors" />
                    </button>
                  </div>
                </>
              ) : (
                // Login/Register buttons for non-logged-in users
                <div className="flex items-center space-x-4">
                  <Link
                    to="/login"
                    className="flex items-center space-x-2 text-sm text-gray-700 hover:text-orange-500 font-medium transition-colors"
                  >
                    <LogIn className="w-4 h-4" />
                    <span>Login</span>
                  </Link>
                  <Link
                    to="/signup"
                    className="flex items-center space-x-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span>Register</span>
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Menu Button and Mobile Icons */}
            <div className="lg:hidden flex items-center space-x-4">
              {user ? (
                // Mobile icons for logged-in users
                <>
                  <Globe className="w-4 h-4 cursor-pointer text-gray-600 hover:text-orange-500 transition-colors" title="Language" />

                  {isBuyer && (
                    <Link to="/buyer/cart" className="relative" title="Shopping Cart">
                      <ShoppingCart className="w-4 h-4 text-gray-600 hover:text-orange-500 transition-colors" />
                      {cartCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                          {cartCount > 9 ? '9+' : cartCount}
                        </span>
                      )}
                    </Link>
                  )}

                  <button className="relative" title="Notifications">
                    <Bell className="w-4 h-4 text-gray-600 hover:text-orange-500 transition-colors" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="relative"
                    title="Profile"
                  >
                    <User className="w-4 h-4 text-gray-600 hover:text-orange-500 transition-colors" />
                  </button>
                </>
              ) : (
                // Mobile login/register buttons for non-logged-in users
                <>
                  <Link
                    to="/login"
                    className="flex items-center space-x-1 text-sm text-gray-700 hover:text-orange-500 font-medium transition-colors"
                  >
                    <LogIn className="w-4 h-4" />
                    <span className="hidden sm:inline">Login</span>
                  </Link>
                  <Link
                    to="/signup"
                    className="flex items-center space-x-1 bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span className="hidden sm:inline">Register</span>
                    <span className="sm:hidden">Sign Up</span>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Profile Dropdown Menu - Only show when user is logged in */}
        {isProfileOpen && user && (
          <div className="absolute right-4 top-full mt-1 w-52 bg-white rounded-lg shadow-xl py-2 border border-gray-100 z-50">
            <div className="px-3 py-2 border-b border-gray-100">
              <p className="text-sm font-bold text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500 mt-0.5">{user?.email}</p>
              <div className="mt-1.5">
                <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full bg-orange-100 text-orange-800">
                  {isBuyer ? 'Buyer' : isSeller ? 'Seller' : 'Admin'}
                </span>
              </div>
            </div>
            <Link
              to={isBuyer ? '/buyer/dashboard' : isSeller ? '/seller/dashboard' : '/admin/dashboard'}
              className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
              onClick={() => setIsProfileOpen(false)}
            >
              <User className="h-3.5 w-3.5 mr-2" />
              Dashboard
            </Link>
            <Link
              to={isBuyer ? '/buyer/profile' : isSeller ? '/seller/profile' : '/admin/profile'}
              className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
              onClick={() => setIsProfileOpen(false)}
            >
              <User className="h-3.5 w-3.5 mr-2" />
              Profile
            </Link>
            <hr className="my-1.5 border-gray-100" />
            <button
              onClick={() => {
                handleLogout();
                setIsProfileOpen(false);
              }}
              className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5 mr-2" />
              Sign Out
            </button>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;